import React, { useState, useRef, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import HtmlEditorWithLines from '../components/HtmlEditorWithLines';

const HtmlViewerPage = () => {
  const [htmlInput, setHtmlInput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isCodeFolded, setIsCodeFolded] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const iframeRef = useRef(null);
  
  // Handle iframe resize
  const handleResize = () => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Iframe height is now handled by flex layout
        console.log('Iframe content loaded');
      }
    } catch (e) {
      console.error('Error resizing iframe:', e);
    }
  };
  
  // Update iframe content when HTML input changes
  useEffect(() => {
    try {
      const iframe = iframeRef.current;
      if (iframe) {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlInput || getDefaultHtml());
        iframeDoc.close();
        
        // Setup resize after load
        if (iframe.contentWindow) {
          iframe.contentWindow.onload = handleResize;
          // Add some time for CSS to be applied
          setTimeout(handleResize, 500);
        }
      }
    } catch (e) {
      setError('Error updating preview: ' + e.message);
    }
  }, [htmlInput]);
  
  // Default HTML template
  const getDefaultHtml = () => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .placeholder { 
              display: flex; 
              height: 300px; 
              align-items: center; 
              justify-content: center;
              border: 2px dashed #ccc;
              border-radius: 8px;
              background-color: #f8f9fa;
            }
            .message {
              text-align: center;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="placeholder">
            <div class="message">
              <h2>HTML Viewer</h2>
              <p>Enter your HTML code in the editor to see the live preview here.</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHtmlInput(e.target.result);
        setError('');
      };
      reader.readAsText(file);
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  const handleCopyHtml = () => {
    navigator.clipboard.writeText(htmlInput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      setError('Failed to copy text: ' + err.message);
    });
  };
  
  const loadExample = () => {
    setHtmlInput(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>HTML Example</title>
  <style>
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      margin: 0;
      padding: 20px;
      background-color: #f5f5f5;
      color: #333;
    }
    .container {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 1px solid #eee;
    }
    h1 {
      color: #2c3e50;
      margin-bottom: 10px;
    }
    .profile {
      display: flex;
      align-items: center;
      margin-bottom: 20px;
    }
    .profile-img {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      background-color: #3498db;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 36px;
      font-weight: bold;
      margin-right: 20px;
    }
    .btn {
      display: inline-block;
      background-color: #3498db;
      color: white;
      padding: 10px 20px;
      text-decoration: none;
      border-radius: 4px;
      transition: background-color 0.3s;
    }
    .btn:hover {
      background-color: #2980b9;
    }
    .features {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      margin-top: 30px;
    }
    .feature {
      flex: 1;
      min-width: 200px;
      padding: 15px;
      border: 1px solid #eee;
      border-radius: 5px;
      background-color: #f9f9f9;
    }
    footer {
      text-align: center;
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #eee;
      color: #777;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <h1>Welcome to HTML Viewer</h1>
      <p>See your HTML code come to life instantly!</p>
    </header>
    
    <div class="profile">
      <div class="profile-img">HV</div>
      <div>
        <h2>HTML Viewer Tool</h2>
        <p>An easy way to visualize your HTML code in real-time.</p>
        <a href="#" class="btn">Learn More</a>
      </div>
    </div>
    
    <div class="features">
      <div class="feature">
        <h3>Live Preview</h3>
        <p>See changes instantly as you type or edit your HTML.</p>
      </div>
      <div class="feature">
        <h3>Code Editor</h3>
        <p>Simple and effective code editor with line numbers.</p>
      </div>
      <div class="feature">
        <h3>Responsive</h3>
        <p>Test how your HTML looks across different screen sizes.</p>
      </div>
    </div>
    
    <footer>
      <p>&copy; 2023 HTML Viewer Tool. All rights reserved.</p>
    </footer>
  </div>
</body>
</html>`);
    setError('');
  };
  
  const clearAll = () => {
    setHtmlInput('');
    setError('');
  };

  // HTML Beautifier function
  const beautifyHtml = () => {
    if (!htmlInput.trim()) return;
    
    try {
      let formatted = htmlInput
        .replace(/></g, '>\n<')
        .replace(/^\s+|\s+$/gm, '')
        .split('\n')
        .filter(line => line.trim() !== '')
        .map(line => line.trim())
        .join('\n');
      
      let indentLevel = 0;
      const indentSize = 2;
      const lines = formatted.split('\n');
      const formattedLines = [];
      
      lines.forEach(line => {
        if (line.match(/<\/(\w+)>/)) {
          indentLevel = Math.max(0, indentLevel - 1);
        }
        
        formattedLines.push(' '.repeat(indentLevel * indentSize) + line);
        
        if (line.match(/<(\w+)[^>]*>/) && !line.match(/<\/(\w+)>/) && !line.match(/<(\w+)[^>]*\/>/)) {
          indentLevel++;
        }
      });
      
      setHtmlInput(formattedLines.join('\n'));
      setError('');
    } catch (e) {
      setError('Error beautifying HTML: ' + e.message);
    }
  };

  // Code folding functions
  const foldHtmlTags = (html) => {
    if (!html.trim()) return html;
    
    // Find opening and closing tags
    const lines = html.split('\n');
    const foldedLines = [];
    const tagStack = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check for opening tags (not self-closing)
      const openTagMatch = trimmedLine.match(/<([a-zA-Z][^>\s]*)[^>]*>/);
      const closeTagMatch = trimmedLine.match(/<\/([a-zA-Z][^>\s]*)/);
      const selfClosingMatch = trimmedLine.match(/<[^>]*\/>/);
      
      if (openTagMatch && !selfClosingMatch && !closeTagMatch) {
        // Opening tag
        const tagName = openTagMatch[1];
        tagStack.push({ tagName, startLine: i, indent: line.match(/^\s*/)[0] });
        foldedLines.push(line + ' ▼');
      } else if (closeTagMatch) {
        // Closing tag
        const tagName = closeTagMatch[1];
        const lastTag = tagStack[tagStack.length - 1];
        
        if (lastTag && lastTag.tagName === tagName) {
          tagStack.pop();
          // Don't add the content lines and closing tag in folded mode
          continue;
        } else {
          foldedLines.push(line);
        }
      } else if (tagStack.length === 0) {
        // Only add lines that are not inside folded blocks
        foldedLines.push(line);
      }
    }
    
    return foldedLines.join('\n');
  };

  // Expand All function - artık hiçbir boyut değişikliği yapmaz
  const expandAll = () => {
    // Sadece kod katlamayı açar
    setIsCodeFolded(false);
  };

  // Collapse All function  
  const collapseAll = () => {
    setIsCodeFolded(!isCodeFolded);
  };
  
  return (
    <div>
      <SEO 
        title="HTML Viewer - Live HTML Preview Tool" 
        description="Paste your HTML code and see it rendered in real-time. Test how your HTML looks with this free online HTML preview tool."
        keywords="html viewer, html preview, live html, html renderer, code preview, test html"
      />
      
      <div className="container" style={{ maxWidth: '%95', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>HTML Viewer</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Paste your HTML code and see it rendered in real-time. Perfect for testing how your HTML looks.
        </p>
        

        <div className="main-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="button-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
            <button 
              onClick={handleUploadClick}
              style={{ 
                padding: '8px 16px', 
                background: '#2196F3', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Upload HTML File
            </button>
            
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".html,.htm"
            />
            
            <button 
              onClick={loadExample}
              style={{ 
                padding: '8px 16px', 
                background: '#9c27b0', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Load Example
            </button>
            
            <button 
              onClick={handleCopyHtml}
              style={{ 
                padding: '8px 16px', 
                background: '#607d8b', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {copySuccess ? 'Copied!' : 'Copy HTML'}
            </button>
            
            <button 
              onClick={clearAll}
              style={{ 
                padding: '8px 16px', 
                background: '#f44336', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear All
            </button>
            
            <button 
              onClick={beautifyHtml}
              style={{ 
                padding: '8px 16px', 
                background: '#4caf50', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Beautify HTML
            </button>
            
            <button 
              onClick={expandAll}
              style={{ 
                padding: '8px 16px', 
                background: '#ff9800', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Expand All
            </button>
            
            <button 
              onClick={collapseAll}
              style={{ 
                padding: '8px 16px', 
                background: '#795548', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {isCodeFolded ? 'Expand Tags' : 'Collapse Tags'}
            </button>
          </div>
          
          {error && (
            <div style={{ color: '#f44336', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px', marginBottom: '10px' }}>
              {error}
            </div>
          )}
          
          <div className="viewer-container" style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row', 
            gap: '20px',
            height: 'calc(100vh - 400px)',
            minHeight: '600px'
          }}>
            <div className="input-section" style={{ 
              flex: '1', 
              display: 'flex', 
              flexDirection: 'column',
              minWidth: window.innerWidth <= 768 ? '100%' : '50%'
            }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '10px' }}>HTML Code</h3>
              <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                overflow: 'hidden',
                flex: '1',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <HtmlEditorWithLines
                  ref={textareaRef}
                  value={isCodeFolded ? foldHtmlTags(htmlInput) : htmlInput}
                  onChange={(e) => setHtmlInput(e.target.value)}
                  placeholder="Enter or paste your HTML code here..."
                  style={{
                    height: '100%',
                    minHeight: '400px',
                    maxHeight: '100%'
                  }}
                  readOnly={isCodeFolded}
                />
              </div>
            </div>
            
            <div className="preview-section" style={{ 
              flex: '1', 
              display: 'flex', 
              flexDirection: 'column',
              minWidth: window.innerWidth <= 768 ? '100%' : '50%'
            }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '10px' }}>Preview</h3>
              <div 
                style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  position: 'relative',
                  flex: '1',
                  minHeight: '400px'
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="HTML Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block',
                    backgroundColor: 'white'
                  }}
                  sandbox="allow-same-origin allow-popups allow-forms"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About HTML Viewer</h2>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            HTML Viewer is a tool that allows you to see how your HTML code renders in a browser in real-time.
            This is useful for testing HTML snippets, debugging, and learning HTML.
          </p>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            Features of this HTML Viewer:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '20px', lineHeight: '1.6' }}>
            <li>Live preview as you type</li>
            <li>Upload HTML files directly</li>
            <li>Sandboxed environment for safe execution</li>
            <li>Code editor with line numbers</li>
          </ul>
          <p style={{ lineHeight: '1.6' }}>
            For security reasons, some features like JavaScript execution may be limited.
            The preview is rendered in a sandboxed iframe to prevent any potential security issues.
          </p>
        </div>
      </div>
      
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .button-container {
            flex-direction: column;
          }
          
          .button-container button {
            width: 100%;
            margin-bottom: 5px;
          }
          
          .viewer-container {
            flex-direction: column !important;
            height: auto !important;
          }
          
          .input-section, .preview-section {
            min-width: 100% !important;
          }
        }
        
        .viewer-container {
          transition: all 0.3s ease;
        }
        
        .input-section, .preview-section {
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default HtmlViewerPage; 